/**
 * Staff Time Clock Guide - Simple Mobile-Friendly Guide
 * Covers: Clock In, Clock Out, View Schedule, Request Time Off
 */
import { 
  Printer, Clock, Calendar, LogIn, LogOut, CalendarDays, 
  CheckCircle, Smartphone
} from 'lucide-react';

export default function TimeClockGuidePage() {
  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        @page {
          size: A4;
          margin: 15mm;
        }
      `}</style>

      {/* Header */}
      <div className="no-print bg-green-700 text-white py-4 px-6 sticky top-0 z-50 shadow-lg">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="w-7 h-7" />
            <div>
              <h1 className="text-lg font-bold">Time Clock Guide</h1>
              <p className="text-green-100 text-xs">Bulaccino Cafe</p>
            </div>
          </div>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-white text-green-700 rounded-lg font-medium hover:bg-green-50 transition-colors text-sm"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto p-4 bg-white text-gray-900">
        
        {/* Title */}
        <div className="text-center py-6 border-b-2 border-green-600 mb-6">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <Clock className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Staff Time Clock Guide</h1>
          <p className="text-gray-600">Bulaccino Cafe - Nadi, Fiji</p>
        </div>

        {/* Important URL */}
        <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 mb-6 text-center">
          <p className="text-green-800 font-bold text-lg">Time Clock URL:</p>
          <p className="text-green-700 font-mono text-lg">bulaccinocafe.com/time-clock</p>
        </div>

        {/* Section 1: How to Clock In */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <LogIn className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">1. How to Clock In</h2>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-7 h-7 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
              <div>
                <p className="font-medium">Open Time Clock</p>
                <p className="text-gray-600 text-sm">Go to <span className="font-mono bg-gray-200 px-1 rounded">bulaccinocafe.com/time-clock</span></p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-7 h-7 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
              <div>
                <p className="font-medium">Enter Your PIN</p>
                <p className="text-gray-600 text-sm">Type your 6-digit PIN</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-7 h-7 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
              <div>
                <p className="font-medium">Tap "Clock In"</p>
                <p className="text-gray-600 text-sm">The green button at the top</p>
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <p className="text-green-800 font-medium">You'll see "Clocked In" confirmation</p>
            </div>
          </div>
        </div>

        {/* Section 2: How to Clock Out */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <LogOut className="w-5 h-5 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">2. How to Clock Out</h2>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-7 h-7 bg-red-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
              <div>
                <p className="font-medium">Open Time Clock</p>
                <p className="text-gray-600 text-sm">Go to <span className="font-mono bg-gray-200 px-1 rounded">bulaccinocafe.com/time-clock</span></p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-7 h-7 bg-red-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
              <div>
                <p className="font-medium">Enter Your PIN</p>
                <p className="text-gray-600 text-sm">Type your 6-digit PIN</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-7 h-7 bg-red-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
              <div>
                <p className="font-medium">Tap "Clock Out"</p>
                <p className="text-gray-600 text-sm">The red button at the top</p>
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <p className="text-green-800 font-medium">You'll see "Clocked Out" confirmation</p>
            </div>
          </div>
        </div>

        {/* Section 3: View Your Schedule */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">3. View Your Schedule</h2>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
              <div>
                <p className="font-medium">Open Time Clock</p>
                <p className="text-gray-600 text-sm">Go to <span className="font-mono bg-gray-200 px-1 rounded">bulaccinocafe.com/time-clock</span></p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
              <div>
                <p className="font-medium">Enter Your PIN</p>
                <p className="text-gray-600 text-sm">Type your 6-digit PIN</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
              <div>
                <p className="font-medium">Tap "My Schedule"</p>
                <p className="text-gray-600 text-sm">Look for the calendar icon</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">4</div>
              <div>
                <p className="font-medium">View Your Shifts</p>
                <p className="text-gray-600 text-sm">Your upcoming shifts are listed by date and time</p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Request Time Off */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-purple-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">4. Request Time Off</h2>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
              <div>
                <p className="font-medium">Open Time Clock</p>
                <p className="text-gray-600 text-sm">Go to <span className="font-mono bg-gray-200 px-1 rounded">bulaccinocafe.com/time-clock</span></p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
              <div>
                <p className="font-medium">Enter Your PIN</p>
                <p className="text-gray-600 text-sm">Type your 6-digit PIN</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
              <div>
                <p className="font-medium">Tap "Request Time Off"</p>
                <p className="text-gray-600 text-sm">Look for the calendar icon with a plus</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">4</div>
              <div>
                <p className="font-medium">Select Dates</p>
                <p className="text-gray-600 text-sm">Pick the start and end date</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">5</div>
              <div>
                <p className="font-medium">Select Reason</p>
                <p className="text-gray-600 text-sm">Choose: Annual Leave, Sick Leave, Personal, or Other</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">6</div>
              <div>
                <p className="font-medium">Add Notes (Optional)</p>
                <p className="text-gray-600 text-sm">Add any details for your manager</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">7</div>
              <div>
                <p className="font-medium">Tap "Submit Request"</p>
                <p className="text-gray-600 text-sm">Your manager will review and approve/deny</p>
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <Smartphone className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <p className="text-amber-800 text-sm">You'll get a notification when your request is approved or denied</p>
            </div>
          </div>
        </div>

        {/* Quick Reference */}
        <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 mt-6">
          <h3 className="font-bold text-green-800 mb-3 text-center">Quick Reference</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-700">Time Clock URL:</span>
              <span className="font-mono text-green-700">bulaccinocafe.com/time-clock</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700">Need help?</span>
              <span className="text-green-700">Ask your manager</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-gray-200 text-center text-gray-500 text-xs">
          <p>Bulaccino Cafe - Staff Time Clock Guide</p>
        </div>
      </div>
    </>
  );
}
